import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tiempoService } from '@/services/tiempo';
import { toast } from '@/components/ui';
import type { TiempoRegistroCreate, TiempoRegistroUpdate } from '@/services/tiempo';

const tiempoKeys = {
  timer: ['tiempo', 'timer'] as const,
  registros: (actividadId?: string) => ['tiempo', 'registros', actividadId] as const,
  resumenDia: (fecha?: string) => ['tiempo', 'resumen', 'dia', fecha] as const,
  resumenSemana: (fecha?: string) => ['tiempo', 'resumen', 'semana', fecha] as const,
};

export function useTimerActivo() {
  return useQuery({
    queryKey: tiempoKeys.timer,
    queryFn: () => tiempoService.getTimerActivo(),
    refetchInterval: (query) => query.state.status === 'error' ? false : 30000,
    refetchIntervalInBackground: false,
  });
}

export function useIniciarTimer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (actividadId: string) => tiempoService.iniciarTimer(actividadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tiempoKeys.timer });
      queryClient.invalidateQueries({ queryKey: ['tiempo', 'registros'] });
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo iniciar el timer');
    },
  });
}

export function usePausarTimer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tiempoService.pausarTimer(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tiempoKeys.timer });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo pausar el timer');
    },
  });
}

export function useReanudarTimer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tiempoService.reanudarTimer(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tiempoKeys.timer });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo reanudar el timer');
    },
  });
}

export function useDetenerTimer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tiempoService.detenerTimer(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tiempoKeys.timer });
      queryClient.invalidateQueries({ queryKey: ['tiempo', 'registros'] });
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Tiempo registrado');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo detener el timer');
    },
  });
}

export function useRegistrarTiempoManual() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TiempoRegistroCreate) => tiempoService.registrarManual(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiempo', 'registros'] });
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Tiempo registrado manualmente');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo registrar el tiempo');
    },
  });
}

export function useRegistrosTiempo(actividadId?: string) {
  return useQuery({
    queryKey: tiempoKeys.registros(actividadId),
    queryFn: () => tiempoService.listarRegistros({ actividad_id: actividadId }),
    enabled: !!actividadId,
  });
}

export function useActualizarRegistroTiempo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TiempoRegistroUpdate }) =>
      tiempoService.actualizarRegistro(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiempo', 'registros'] });
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Registro actualizado');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo actualizar el registro');
    },
  });
}

export function useEliminarRegistroTiempo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tiempoService.eliminarRegistro(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiempo', 'registros'] });
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Registro eliminado');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo eliminar el registro');
    },
  });
}

export function useResumenDia(fecha?: string) {
  return useQuery({
    queryKey: tiempoKeys.resumenDia(fecha),
    queryFn: () => tiempoService.resumenDia(fecha),
  });
}

export function useResumenSemana(fechaInicio?: string) {
  return useQuery({
    queryKey: tiempoKeys.resumenSemana(fechaInicio),
    queryFn: () => tiempoService.resumenSemana(fechaInicio),
  });
}
